import React, { ReactNode, useState } from 'react';
import { Checkbox, Input, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useReviewMode } from './index';

const { TextArea } = Input;

interface ReviewFieldWrapperProps {
  children: ReactNode;
  fieldPath: string;
  fieldLabel: string;
  stepName?: string;
  layout?: 'inline' | 'stacked'; // inline = checkbox ao lado (padrão), stacked = checkbox acima (para tabelas)
  linkInline?: boolean; // true = link ao lado do conteúdo (para títulos), false/undefined = link abaixo (padrão para campos)
  allowEditAllInCorrection?: boolean; // Em modo correction, permite editar mesmo campos não marcados (para IdentificacaoStep)
  // Form.Item injected props (from Ant Design Form)
  value?: any;
  onChange?: (value: any) => void;
  [key: string]: any; // Allow any other props from Form.Item
}

/**
 * Wrapper component que adiciona funcionalidade de revisão a um campo
 * Em modo de revisão, mostra um checkbox para marcar o campo como necessitando correção
 */
export function ReviewFieldWrapper({
  children,
  fieldPath,
  fieldLabel,
  stepName = '',
  layout = 'inline',
  linkInline = false,
  allowEditAllInCorrection = false,
  value,
  onChange,
  ...restProps // Capture any other props from Form.Item
}: ReviewFieldWrapperProps) {
  const { mode, isFieldMarked, addCorrection, removeCorrection, selectedCorrections } = useReviewMode();

  // Helper to clone children with form props (value, onChange, etc)
  const cloneChildWithFormProps = (child: ReactNode, extraProps?: Record<string, any>) => {
    if (React.isValidElement(child)) {
      const formProps: Record<string, any> = {};
      if (value !== undefined) formProps['value'] = value;
      if (onChange !== undefined) {
        formProps['onChange'] = onChange;
      }
      // Forward any other props from Form.Item (like onBlur, id, etc)
      Object.keys(restProps).forEach(key => {
        formProps[key] = restProps[key];
      });
      if (extraProps) Object.assign(formProps, extraProps);

      return React.cloneElement(child, formProps);
    }
    return child;
  };
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [editErrorMessage, setEditErrorMessage] = useState('');

  const isMarked = isFieldMarked(fieldPath);

  // Buscar a correção atual para este campo
  const currentCorrection = selectedCorrections.find(c => c.fieldPath === fieldPath);

  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      // Abrir modal para adicionar mensagem de erro
      setShowModal(true);
    } else {
      // Remover correção
      removeCorrection(fieldPath);
    }
  };

  const handleModalOk = () => {
    if (!errorMessage.trim()) {
      return;
    }

    addCorrection({
      fieldPath,
      fieldLabel,
      stepName,
      errorMessage: errorMessage.trim(),
      correctionStatus: 'Pending',
    });

    setShowModal(false);
    setErrorMessage('');
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setErrorMessage('');
  };

  const handleViewCorrection = () => {
    if (currentCorrection) {
      setEditErrorMessage(currentCorrection.errorMessage);
      setShowEditModal(true);
    }
  };

  const handleEditModalOk = () => {
    if (!editErrorMessage.trim()) {
      return;
    }

    // Remover a correção antiga
    removeCorrection(fieldPath);

    // Adicionar a correção atualizada
    addCorrection({
      fieldPath,
      fieldLabel,
      stepName,
      errorMessage: editErrorMessage.trim(),
      correctionStatus: 'Pending',
    });

    setShowEditModal(false);
    setEditErrorMessage('');
  };

  const handleEditModalCancel = () => {
    setShowEditModal(false);
    setEditErrorMessage('');
  };

  // Modo 'correction': Mostrar alertas para campos marcados e desabilitar os não marcados
  if (mode === 'correction') {
    const { reviewContext } = useReviewMode();
    const pendingCorrection = reviewContext?.pendingCorrections?.find(c => c.fieldPath === fieldPath);
    const hasPendingCorrections = reviewContext?.pendingCorrections && reviewContext.pendingCorrections.length > 0;

    // Campo tem correção pendente: mostrar observação em vermelho (campo editável)
    if (pendingCorrection) {
      return (
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: 12, color: '#ff4d4f', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            <ExclamationCircleOutlined />
            <span style={{fontWeight: 650}}>{pendingCorrection.errorMessage}</span>
          </div>
          {cloneChildWithFormProps(children)}
        </div>
      );
    }

    // Se não há correções pendentes (devolução externa), todos os campos são editáveis
    if (!hasPendingCorrections) {
      return <>{cloneChildWithFormProps(children)}</>;
    }

    // Se allowEditAllInCorrection=true, permite editar mesmo sem correção pendente
    // (usado no IdentificacaoStep para dar mais liberdade ao usuário)
    if (allowEditAllInCorrection) {
      return <>{cloneChildWithFormProps(children)}</>;
    }

    // Campo NÃO tem correção: desabilitar (mas ainda precisa das props do form para mostrar valor)
    return (
      <div style={{ opacity: 0.6 }}>
        {cloneChildWithFormProps(children, { disabled: true, readOnly: true })}
      </div>
    );
  }

  if (mode !== 'review') {
    // Modo normal: renderiza o children COM as props do form (value, onChange)
    return <>{cloneChildWithFormProps(children)}</>;
  }

  // Modo de revisão: adiciona checkbox
  // Layout 'stacked' para tabelas grandes
  if (layout === 'stacked') {
    return (
      <div style={{ position: 'relative' }}>
        <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Checkbox
            checked={isMarked}
            onChange={(e) => handleCheckboxChange(e.target.checked)}
          />
          <span style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>
            {fieldLabel}
          </span>
          {isMarked && currentCorrection && (
            <a
              onClick={handleViewCorrection}
              style={{
                fontSize: 12,
                color: '#1890ff',
                cursor: 'pointer',
                fontWeight: 500,
                textDecoration: 'none',
                marginLeft: 8,
              }}
            >
              Ver correção sugerida
            </a>
          )}
        </div>
        <div style={{ opacity: isMarked ? 0.6 : 1 }}>
          {cloneChildWithFormProps(children)}
        </div>

        {/* Modals */}
        <Modal
          title="Adicionar Correção"
          open={showModal}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          okText="Adicionar"
          cancelText="Cancelar"
          okButtonProps={{ disabled: !errorMessage.trim() }}
        >
          <div style={{ marginBottom: 16 }}>
            <p style={{ margin: 0, marginBottom: 8, fontWeight: 500 }}>
              Campo: {fieldLabel}
            </p>

          </div>

          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#595959' }}>
              Correção sugerida:
            </label>
          </div>

          <TextArea
            value={errorMessage}
            onChange={(e) => setErrorMessage(e.target.value)}
            placeholder="Descreva o que precisa ser corrigido neste campo..."
            rows={4}
            autoFocus
          />
        </Modal>

        <Modal
          title="Visualizar/Editar Correção"
          open={showEditModal}
          onOk={handleEditModalOk}
          onCancel={handleEditModalCancel}
          okText="Salvar Alterações"
          cancelText="Cancelar"
          okButtonProps={{ disabled: !editErrorMessage.trim() }}
        >
          <div style={{ marginBottom: 16 }}>
            <p style={{ margin: 0, marginBottom: 8, fontWeight: 500 }}>
              Campo: {fieldLabel}
            </p>
          </div>

          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: '#595959' }}>
              Correção sugerida:
            </label>
          </div>

          <TextArea
            value={editErrorMessage}
            onChange={(e) => setEditErrorMessage(e.target.value)}
            placeholder="Descreva o que precisa ser corrigido neste campo..."
            rows={4}
            autoFocus
          />

        </Modal>
      </div>
    );
  }

  // Layout 'inline' padrão para campos normais
  // Se linkInline=true, link aparece ao lado (para títulos de tabela)
  // Se linkInline=false, link aparece embaixo (para campos normais)
  if (linkInline) {
    return (
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Checkbox
            checked={isMarked}
            onChange={(e) => handleCheckboxChange(e.target.checked)}
          />
          <div style={{ opacity: isMarked ? 0.6 : 1 }}>
            {cloneChildWithFormProps(children)}
          </div>
          {isMarked && currentCorrection && (
            <a
              onClick={handleViewCorrection}
              style={{
                fontSize: 12,
                color: '#1890ff',
                cursor: 'pointer',
                fontWeight: 500,
                textDecoration: 'none',
                marginLeft: 8,
              }}
            >
              Ver correção sugerida
            </a>
          )}
        </div>

      {/* Modal para adicionar nova correção */}
      <Modal
        title="Adicionar Correção"
        open={showModal}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Adicionar"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !errorMessage.trim() }}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ margin: 0, marginBottom: 8, fontWeight: 500 }}>
            Campo: {fieldLabel}
          </p>
        </div>

        <TextArea
          value={errorMessage}
          onChange={(e) => setErrorMessage(e.target.value)}
          placeholder="Descreva o que precisa ser corrigido neste campo..."
          rows={4}
          autoFocus
        />
      </Modal>

      {/* Modal para visualizar/editar correção existente */}
      <Modal
        title="Visualizar/Editar Correção"
        open={showEditModal}
        onOk={handleEditModalOk}
        onCancel={handleEditModalCancel}
        okText="Salvar Alterações"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !editErrorMessage.trim() }}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ margin: 0, marginBottom: 8, fontWeight: 500 }}>
            Campo: {fieldLabel}
          </p>
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: '#595959' }}>
            Correção sugerida:
          </label>
        </div>

        <TextArea
          value={editErrorMessage}
          onChange={(e) => setEditErrorMessage(e.target.value)}
          placeholder="Descreva o que precisa ser corrigido neste campo..."
          rows={4}
          autoFocus
        />
      </Modal>
    </div>
    );
  }

  // Layout padrão: link aparece embaixo do conteúdo
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ paddingTop: 1.8 }}>
          <Checkbox
            checked={isMarked}
            onChange={(e) => handleCheckboxChange(e.target.checked)}
          />
        </div>
        <div style={{ flex: 1, opacity: isMarked ? 0.6 : 1 }}>
          {cloneChildWithFormProps(children)}
          {isMarked && currentCorrection && (
            <div style={{ marginTop: 4 }}>
              <a
                onClick={handleViewCorrection}
                style={{
                  fontSize: 12,
                  color: '#1890ff',
                  cursor: 'pointer',
                  fontWeight: 500,
                  textDecoration: 'none',
                }}
              >
                Ver correção sugerida
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Modal para adicionar nova correção */}
      <Modal
        title="Adicionar Correção"
        open={showModal}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Adicionar"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !errorMessage.trim() }}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ margin: 0, marginBottom: 8, fontWeight: 500 }}>
            Campo: {fieldLabel}
          </p>
        </div>

        <TextArea
          value={errorMessage}
          onChange={(e) => setErrorMessage(e.target.value)}
          placeholder="Descreva o que precisa ser corrigido neste campo..."
          rows={4}
          autoFocus
        />
      </Modal>

      {/* Modal para visualizar/editar correção existente */}
      <Modal
        title="Visualizar/Editar Correção"
        open={showEditModal}
        onOk={handleEditModalOk}
        onCancel={handleEditModalCancel}
        okText="Salvar Alterações"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !editErrorMessage.trim() }}
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ margin: 0, marginBottom: 8, fontWeight: 500 }}>
            Campo: {fieldLabel}
          </p>
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: '#595959' }}>
            Correção sugerida:
          </label>
        </div>

        <TextArea
          value={editErrorMessage}
          onChange={(e) => setEditErrorMessage(e.target.value)}
          placeholder="Descreva o que precisa ser corrigido neste campo..."
          rows={4}
          autoFocus
        />
      </Modal>
    </div>
  );
}
